/**
 * Составной адрес (ТЗ п. 5.2.1, форма приказа МЭР № 530).
 *
 * ГРАНИЦА ИНТЕГРАЦИИ. Адрес должника никуда не передаётся: подсказок здесь
 * нет вовсе. Ввод — по составным полям формы, а строка для заявления
 * собирается из них автоматически, чтобы не заполнять одно и то же дважды.
 *
 * Субъект РФ выбирается из списка, а не вводится текстом: по нему подставляется
 * арбитражный суд (правило R-01), и опечатка здесь стоила бы неверной
 * подсудности. Список берётся из того же справочника, откуда берутся суды,
 * поэтому «субъект есть, а суда по нему нет» невозможно.
 */

import { composeAddress, setAddressFull, updateAddressPart, type Address } from '@bd/domain';
import { Field } from './index.js';

export interface RegionOption {
  code: string;
  name: string;
}

export interface AddressFieldProps {
  label: string;
  value: Address;
  onChange: (address: Address) => void;
  required?: boolean;
  error?: string | null;
  readOnly?: boolean;
  /** Субъекты РФ из справочника судов */
  regions: RegionOption[];
  /** Код субъекта — по нему предзаполняется суд (правило R-01) */
  onRegionDetected?: (regionCode: string) => void;
}

/** Части адреса в порядке формы приказа № 530. */
const PARTS: Array<[key: keyof Address, label: string, hint?: string]> = [
  ['postalCode', 'Индекс'],
  ['area', 'Район'],
  ['city', 'Город'],
  ['settlement', 'Населённый пункт', 'село, посёлок и так далее'],
  ['street', 'Улица', 'проспект, переулок и так далее'],
  ['house', 'Дом (владение)'],
  ['block', 'Корпус (строение)'],
  ['flat', 'Квартира (офис)'],
];

export function AddressField({
  label,
  value,
  onChange,
  required,
  error,
  readOnly,
  regions,
  onRegionDetected,
}: AddressFieldProps) {
  const setPart = (key: keyof Address, partValue: string) => onChange(updateAddressPart(value, key, partValue));

  const pickRegion = (code: string) => {
    const region = regions.find((r) => r.code === code);
    onChange(updateAddressPart(value, 'region', region?.name ?? ''));
    if (code) onRegionDetected?.(code);
  };

  /** Код выбранного субъекта восстанавливается по наименованию. */
  const selectedRegionCode = regions.find((r) => r.name === value.region)?.code ?? '';

  return (
    <div className="address-field">
      <Field
        label={label}
        error={error}
        required={required}
        hint={
          value.fullManual
            ? 'Строка правилась вручную и больше не пересобирается из полей'
            : 'Собирается из полей ниже. Можно поправить — тогда автосборка отключится'
        }
      >
        <div className="field-row">
          <input
            value={value.full ?? ''}
            readOnly={readOnly}
            placeholder="Заполните поля ниже — строка соберётся сама"
            onChange={(e) => onChange(setAddressFull(value, e.target.value))}
          />
          {value.fullManual && (
            <button
              type="button"
              className="link-button"
              disabled={readOnly}
              title="Собрать строку из составных полей заново"
              onClick={() => onChange({ ...value, full: composeAddress(value), fullManual: false })}
            >
              Собрать заново
            </button>
          )}
        </div>
      </Field>

      <div className="address-parts">
        <label className="field field-small address-region">
          <span className="field-label">
            Субъект Российской Федерации
            <span className="required" title="Обязательное поле"> *</span>
          </span>
          <select value={selectedRegionCode} disabled={readOnly} onChange={(e) => pickRegion(e.target.value)}>
            <option value="">— выберите субъект —</option>
            {regions.map((region) => (
              <option key={region.code} value={region.code}>
                {region.name}
              </option>
            ))}
          </select>
          <span className="field-hint">По нему подставляется арбитражный суд</span>
        </label>

        {PARTS.map(([key, partLabel, hint]) => (
          <label className="field field-small" key={key}>
            <span className="field-label">{partLabel}</span>
            <input
              value={(value[key] as string) ?? ''}
              readOnly={readOnly}
              onChange={(e) => setPart(key, e.target.value)}
            />
            {hint && <span className="field-hint">{hint}</span>}
          </label>
        ))}
      </div>
    </div>
  );
}
